import { isDefined } from "@clipboard-health/util-ts";
import { type WorkerTripData } from "@src/appV2/redesign/DailyView/api/useGetWorkerTrips";
import { addMinutes, differenceInMinutes, isAfter } from "date-fns";

const METERS_PER_MILE = 1609;
const NEARBY_DISTANCE_METERS = METERS_PER_MILE;
const NEARBY_MAX_STALENESS_MINUTES = 2;
const MOVING_MIN_DURATION_MINUTES = 10;
const MOVING_ETA_BUFFER_MINUTES = 60;

export const NO_SHOW_WARNING_TYPE = {
  workerNearby: "Worker_Nearby",
  workerMoving: "Worker_Moving",
} as const;

export type NoShowWarningType = (typeof NO_SHOW_WARNING_TYPE)[keyof typeof NO_SHOW_WARNING_TYPE];

/**
 * Determines whether a no-show warning should be shown based on the worker's live trip data.
 *
 * Returns `"Worker_Nearby"` if the worker is within 1 mile and location is fresh.
 * Returns `"Worker_Moving"` if the worker has been actively moving for ≥10 min and will
 * arrive within 60 minutes of shift start.
 * Returns `null` if no warning conditions are met (proceed directly to the NCNS form).
 *
 * Condition 1 takes priority over condition 2.
 */
export function getNoShowWarningType(
  trip: WorkerTripData | undefined,
  shiftStart: string | Date | undefined
): NoShowWarningType | null {
  if (!isDefined(trip)) {
    return null;
  }

  const {
    currentEtaDistance,
    currentEtaDuration,
    latestWorkerMovement,
    latestWorkerMovementTransitionedAt,
    lastLocationReceivedByServerAt,
  } = trip;

  // Condition 1: Worker is within 1 mile with a fresh, known location
  if (currentEtaDistance < NEARBY_DISTANCE_METERS && isDefined(lastLocationReceivedByServerAt)) {
    const stalenessMinutes = differenceInMinutes(
      new Date(),
      new Date(lastLocationReceivedByServerAt)
    );
    if (stalenessMinutes <= NEARBY_MAX_STALENESS_MINUTES && latestWorkerMovement !== "UNKNOWN") {
      return NO_SHOW_WARNING_TYPE.workerNearby;
    }
  }

  // Condition 2: Worker has been continuously moving for ≥10 min and will arrive within 60 min of shift start
  if (latestWorkerMovement === "MOVING" && isDefined(shiftStart)) {
    const movingDurationMinutes = isDefined(latestWorkerMovementTransitionedAt)
      ? differenceInMinutes(new Date(), new Date(latestWorkerMovementTransitionedAt))
      : 0;

    if (movingDurationMinutes >= MOVING_MIN_DURATION_MINUTES) {
      const shiftStartDate = shiftStart instanceof Date ? shiftStart : new Date(shiftStart);
      const estimatedArrival = addMinutes(new Date(), currentEtaDuration);
      const deadline = addMinutes(shiftStartDate, MOVING_ETA_BUFFER_MINUTES);
      if (!isAfter(estimatedArrival, deadline)) {
        return NO_SHOW_WARNING_TYPE.workerMoving;
      }
    }
  }

  return null;
}
